import { Trans, useLingui } from "@lingui/macro";
import { X } from "lucide-react";

import { cn } from "@app/lib/utils";
import type { PinnedQuestion } from "@app/zustand/dashboard-pathway";
import type { QuestionPreset } from "./labels";

/**
 * The question chips.
 *
 * A question is NOT a card, and that distinction is the whole point: it is a preset that shows its
 * own mechanics. Clicking one reconfigures the cards and the dimension chips change under the
 * reader's eyes, so after three questions they have understood they can remove a chip themselves and
 * no longer need the bar. A menu of hard-written questions is a menu you cannot get out of.
 *
 * Nothing here names a field. Each preset is expressed in terms of what the ontology declares — the
 * act (`temporal_role = current`), the interval axis, the episode's own outcome — so a preset simply
 * does not appear on a speciality that declares none of it.
 */

export default function QuestionBar({
  presets,
  pinned,
  activeId,
  onApply,
  onApplyPinned,
  onUnpin,
}: {
  presets: QuestionPreset[];
  pinned: PinnedQuestion[];
  activeId: string | null;
  onApply: (preset: QuestionPreset) => void;
  onApplyPinned: (question: PinnedQuestion) => void;
  onUnpin: (id: string) => void;
}) {
  const { t } = useLingui();
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {presets.map((preset) => (
        <button
          key={preset.id}
          type="button"
          onClick={() => onApply(preset)}
          aria-pressed={activeId === preset.id}
          className={cn(
            "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
            activeId === preset.id
              ? "border-primary bg-primary text-white"
              : "border-slate-200 bg-white text-slate-600 hover:border-primary/50 hover:text-primary",
          )}
        >
          {preset.label}
        </button>
      ))}
      {pinned.map((question) => (
        <span
          key={question.id}
          className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 py-1 pl-3 pr-1 text-xs font-medium text-primary"
        >
          <button type="button" onClick={() => onApplyPinned(question)}>
            {question.label}
          </button>
          <button
            type="button"
            aria-label={t`Retirer la question épinglée`}
            onClick={() => onUnpin(question.id)}
            className="rounded-full p-0.5 hover:bg-primary/15"
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </span>
      ))}
      {presets.length === 0 && pinned.length === 0 && (
        <span className="text-2xs text-slate-400">
          <Trans>Aucune question prédéfinie pour cette spécialité</Trans>
        </span>
      )}
    </div>
  );
}
